import { type ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface PageHeaderProps {
  /** The page title, at `title` (20 px) on desktop, `headline` (25) on phone. */
  readonly title: ReactNode;
  /** One line of context — the scope, the period, the count. */
  readonly description?: ReactNode;
  /** A `Breadcrumb`, above the title. */
  readonly breadcrumb?: ReactNode;
  /**
   * The page's actions. Exactly ONE of them is a primary — one primary per
   * SURFACE, and a drawer or dialog opened from here owns its own.
   */
  readonly actions?: ReactNode;
  /** Chips or readouts that qualify the title — the period, the scope. */
  readonly meta?: ReactNode;
  /** `worker` uses the phone's title scale and drops the breadcrumb row. */
  readonly surface?: 'admin' | 'worker';
  readonly className?: string;
}

/**
 * The top of a page: where you are, what it holds, and the one thing you are
 * most likely here to do.
 */
export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
  meta,
  surface = 'admin',
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('flex min-w-0 flex-col gap-2', className)}>
      {surface === 'admin' && breadcrumb !== undefined ? breadcrumb : null}

      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className={surface === 'worker' ? 'text-headline text-ink' : 'text-title text-ink'}>
            {title}
          </h1>
          {description !== undefined ? (
            <p className="mt-1 text-body-compact text-ink-muted">{description}</p>
          ) : null}
        </div>
        {actions !== undefined ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>

      {meta !== undefined ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">{meta}</div>
      ) : null}
    </header>
  );
}
